/**
 * @fileoverview EnrichmentWorkflowPanel — UI for creating an enrichment run
 * that updates selected fields on existing parcels using AI transforms.
 * @module import-editor/components
 *
 * Responsibilities:
 * - Let the user select which fields to enrich from the template's field mappings
 * - Let the user optionally filter by jurisdiction (department)
 * - Show a cost estimate before running (dry-run mode)
 * - Create the enrichment run via the provided onCreateEnrichmentRun callback
 *
 * Dependencies:
 * - React (useState, useMemo)
 * - lucide-react icons
 * - getTargetFieldOptions / getTargetFieldLabel from ../utils/targetFieldOptions
 * - ImportDataMode, FieldMapping types from @firesquirrel/local-db
 *
 * Architecture:
 * - Presentational component: all data arrives via props, all side effects
 *   are delegated to callbacks. No API calls, no Dexie reads.
 *
 * Notes:
 * - Only fields with AI transforms are selectable for enrichment.
 * - The cost estimate is based on estimatedCandidates from the backend
 *   dry-run response and per-call cost constants.
 */

import { useState, useMemo, useRef } from 'react';
import {
  Sparkles,
  AlertCircle,
  Loader2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import type { FieldMapping, ImportDataMode } from '@firesquirrel/local-db';
import { getTargetFieldLabel } from '../utils/targetFieldOptions';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** AI transform types that are eligible for enrichment. */
const ENRICHMENT_TRANSFORM_TYPES = new Set([
  'aiParse',
  'aiSplit',
  'aiClassify',
]);

/** Approximate cost per row for cost estimation. */
const AI_COST_PER_ROW = 0.002;

export interface CreateEnrichmentRunParams {
  templateId: string;
  targetFields: string[];
  jurisdictionFilter?: string;
  isDryRun?: boolean;
  importName?: string;
  importDescription?: string;
}

export interface CreateEnrichmentRunResult {
  runId: string;
  status: string;
  estimatedCandidates: number;
}

export interface EnrichmentWorkflowPanelProps {
  /** The import template ID with AI field mappings. */
  templateId: string;
  /** The template's field mappings — used to determine which fields are enrichable. */
  fieldMappings: FieldMapping[];
  /** Data mode for target field label resolution. */
  dataMode: ImportDataMode;
  /** Optional list of departments for jurisdiction filtering. */
  departments?: Array<{ _id: string; name: string }>;
  /** Callback to create the enrichment run. */
  onCreateEnrichmentRun: (params: CreateEnrichmentRunParams) => Promise<CreateEnrichmentRunResult>;
  /** Called when the enrichment run is created successfully. */
  onEnrichmentRunCreated?: (result: CreateEnrichmentRunResult) => void;
}

// ---------------------------------------------------------------------------
// Helper: determine which fields are enrichable
// ---------------------------------------------------------------------------

export function getEnrichableFields(fieldMappings: FieldMapping[]): FieldMapping[] {
  return fieldMappings.filter(
    m => m.transform?.type && ENRICHMENT_TRANSFORM_TYPES.has(m.transform.type),
  );
}

function getTransformIcon(type?: string): JSX.Element {
  switch (type) {
    case 'aiParse':
    case 'aiSplit':
    case 'aiClassify':
      return <Sparkles className="h-3 w-3 text-purple-600" />;
    default:
      return <AlertCircle className="h-3 w-3 text-gray-400" />;
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * @component EnrichmentWorkflowPanel
 *
 * Purpose:
 * - Provide a UI for creating an enrichment run that updates selected fields
 *   on existing parcels using AI transforms.
 *
 * Props:
 * - templateId: the import template ID
 * - fieldMappings: the template's field mappings
 * - dataMode: for target field label resolution
 * - departments: optional list for jurisdiction filtering
 * - onCreateEnrichmentRun: callback to create the run
 * - onEnrichmentRunCreated: callback when run is created
 *
 * Behavior:
 * - Lists enrichable fields (those with AI transforms) as checkboxes.
 * - Optional jurisdiction dropdown filters which parcels to enrich.
 * - "Estimate Cost" button runs a dry-run to get candidate count.
 * - "Start Enrichment" button creates the actual run.
 * - Shows cost estimate based on candidate count and transform types.
 *
 * Data Source:
 * - local (all data via props)
 *
 * Constraints:
 * - No API calls, no Dexie reads — all side effects via callbacks.
 */
export function EnrichmentWorkflowPanel({
  templateId,
  fieldMappings,
  dataMode,
  departments,
  onCreateEnrichmentRun,
  onEnrichmentRunCreated,
}: EnrichmentWorkflowPanelProps): JSX.Element {
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [jurisdictionFilter, setJurisdictionFilter] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(true);
  const [busyAction, setBusyAction] = useState<'estimate' | 'start' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<CreateEnrichmentRunResult | null>(null);
  // Track the field+jurisdiction signature of the in-flight estimate
  // request so stale results (from a superseded selection) are discarded.
  const estimateSignatureRef = useRef<string | null>(null);

  const selectionSignature = () =>
    `${Array.from(selectedFields).sort().join(',')}|${jurisdictionFilter}`;

  const enrichableFields = useMemo(() => getEnrichableFields(fieldMappings), [fieldMappings]);

  const selectedFieldList = useMemo(
    () => enrichableFields.filter(f => selectedFields.has(f.targetField)),
    [enrichableFields, selectedFields],
  );

  const aiCount = selectedFieldList.filter(
    f => f.transform?.type && ['aiParse', 'aiSplit', 'aiClassify'].includes(f.transform.type),
  ).length;

  // Cost estimate based on the dry-run response
  const estimatedCost = useMemo(() => {
    if (!estimate) return null;
    const candidates = estimate.estimatedCandidates;
    return aiCount * candidates * AI_COST_PER_ROW;
  }, [estimate, aiCount]);

  const toggleField = (targetField: string) => {
    setSelectedFields(prev => {
      const next = new Set(prev);
      if (next.has(targetField)) next.delete(targetField);
      else next.add(targetField);
      return next;
    });
    setEstimate(null);
    // Invalidate any in-flight estimate so its stale result is discarded.
    estimateSignatureRef.current = null;
  };

  const handleEstimateCost = async () => {
    if (selectedFields.size === 0) return;
    const signature = selectionSignature();
    estimateSignatureRef.current = signature;
    setBusyAction('estimate');
    setError(null);
    try {
      const result = await onCreateEnrichmentRun({
        templateId,
        targetFields: Array.from(selectedFields),
        jurisdictionFilter: jurisdictionFilter || undefined,
        isDryRun: true,
      });
      // Discard stale results if the user changed fields/jurisdiction
      // while the request was in flight.
      if (estimateSignatureRef.current !== signature) return;
      setEstimate(result);
    } catch (err: any) {
      if (estimateSignatureRef.current !== signature) return;
      setError(err?.message ?? 'Failed to estimate cost');
    } finally {
      setBusyAction(null);
    }
  };

  const handleStartEnrichment = async () => {
    if (selectedFields.size === 0) return;
    setBusyAction('start');
    setError(null);
    try {
      const result = await onCreateEnrichmentRun({
        templateId,
        targetFields: Array.from(selectedFields),
        jurisdictionFilter: jurisdictionFilter || undefined,
        isDryRun: false,
      });
      onEnrichmentRunCreated?.(result);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to start enrichment run');
    } finally {
      setBusyAction(null);
    }
  };

  if (enrichableFields.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
        <div className="flex items-center gap-2 mb-1">
          <AlertCircle className="h-4 w-4 text-amber-500" />
          <span className="font-medium text-gray-700">No enrichable fields</span>
        </div>
        <p className="text-xs">
          This template has no field mappings with AI transforms.
          Add AI parse/split/classify transforms to field mappings before using
          enrichment.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(prev => !prev)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-600" />
          <span className="text-sm font-medium text-gray-800">
            Enrich Existing Parcels
          </span>
          <span className="text-xs text-gray-400">
            ({enrichableFields.length} enrichable field{enrichableFields.length !== 1 ? 's' : ''})
          </span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Field selection */}
          <div>
            <h4 className="text-xs font-medium text-gray-700 mb-2">
              Select fields to enrich
            </h4>
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {enrichableFields.map(field => {
                const checked = selectedFields.has(field.targetField);
                return (
                  <label
                    key={field.targetField}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleField(field.targetField)}
                      className="h-3.5 w-3.5 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    {getTransformIcon(field.transform?.type)}
                    <span className="text-xs text-gray-700">
                      {getTargetFieldLabel(field.targetField, dataMode)}
                    </span>
                    <span className="text-[10px] text-gray-400 font-mono">
                      {field.transform?.type}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Jurisdiction filter — optional department scoping.
              The backend supports jurisdictionFilter (maps to
              departmentId on inspectableEntities), but neither
              tenant-web nor system-web currently exposes a departments
              list in the import wizard context. The dropdown is gated
              behind the departments prop so it stays hidden until a
              caller wires one in. tenant-web is single-tenant so this
              is primarily relevant for system-web. */}
          {departments && departments.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-gray-700 mb-2">
                Jurisdiction filter (optional)
              </h4>
              <select
                value={jurisdictionFilter}
                onChange={e => { setJurisdictionFilter(e.target.value); setEstimate(null); estimateSignatureRef.current = null; }}
                className="w-full max-w-xs border border-gray-200 rounded-sm px-2 py-1.5 text-xs text-gray-700"
              >
                <option value="">All jurisdictions</option>
                {departments.map(d => (
                  <option key={d._id} value={d._id}>{d.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Cost estimate */}
          {estimate && (
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-medium text-blue-800">Cost Estimate</span>
              </div>
              <div className="text-xs text-blue-700 space-y-0.5">
                <p>
                  <strong>{estimate.estimatedCandidates.toLocaleString()}</strong> parcels will be enriched
                </p>
                {aiCount > 0 && (
                  <p>
                    {aiCount} field{aiCount !== 1 ? 's' : ''} via AI
                    (~${(aiCount * estimate.estimatedCandidates * AI_COST_PER_ROW).toFixed(2)})
                  </p>
                )}
                {estimatedCost !== null && (
                  <p className="font-medium pt-1 border-t border-blue-200 mt-1">
                    Estimated total: ~${estimatedCost.toFixed(2)}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <p className="text-xs text-red-600" role="alert">{error}</p>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleEstimateCost}
              disabled={selectedFields.size === 0 || busyAction !== null}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-60 rounded-md transition-colors"
            >
              {busyAction === 'estimate' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Estimate Cost
            </button>
            <button
              type="button"
              onClick={handleStartEnrichment}
              disabled={selectedFields.size === 0 || busyAction !== null}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-60 rounded-md transition-colors"
            >
              {busyAction === 'start' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Start Enrichment
            </button>
            {selectedFields.size > 0 && (
              <span className="text-xs text-gray-500">
                {selectedFields.size} field{selectedFields.size !== 1 ? 's' : ''} selected
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
